import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import DataTransferObject from '@/Communication/DataTransferObject';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import FactoryRefreshedMessage from '@/Messages/FactoryRefreshedMessage';
import ResourceManager from '@/Management/ResourceManager';
import DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import CheckedItemDTOType from '@/Communication/CheckedItemDTO';
import CheckedItemsDTOType from '@/Communication/CheckedItemsDTO';
import TabStrip2ViewModelType from '@/ViewModels/Controls/TabStrip2ViewModel';
import TabStripTab2ViewModelType from '@/ViewModels/Controls/TabStripTab2ViewModel';
import * as CoreFrameInterfaces from '#/CoreFrameInterfaces';
import PrincipalsAndUserGroupsDTO from '@/Communication/PrincipalsAndUserGroupsDTO';
import MemberOfsDTOType from '@/Communication/MemberOfsDTO';
import { IRoleAssignmentViewModelParameters } from '#/DefaultViewExtensionInterfaces';
import { IDVGItemDTO } from "coreframetypings/dto";

export default class RoleAssignmentViewModel extends ControlViewModel {

    private TabStrip2ViewModel = ComponentManager.getViewModel<typeof TabStrip2ViewModelType>('TabStrip2ViewModel');
    private TabStripTab2ViewModel = ComponentManager.getViewModel<typeof TabStripTab2ViewModelType>('TabStripTab2ViewModel');
    private LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private CheckedItemsDTOModel = ComponentManager.getModel<typeof CheckedItemsDTOType>('CheckedItemsDTO');
    private CheckedItemDTOModel = ComponentManager.getModel<typeof CheckedItemDTOType>('CheckedItemDTO');

    /**
    * Der Kontext
    */
    public context: DetailViewContext;

    /**
     * Der Name des DetailViewItems, für welches das Control erstellt wurde
     */
    public detailViewItemName: string;

    /**
    * Das PrincipalsAndUserGroupsDTO
    */
    public PrincipalsAndUserGroupsDTO: KnockoutObservable<PrincipalsAndUserGroupsDTO>;

    /**
    * Verwaltet die selektierten Werte
    */
    public values: KnockoutObservable<CoreFrameInterfaces.RoleAssignmentValue[]>;

    /**
    * Die Namen der erzeugten RoleAssignmentList-Factories
    */
    public names: Array<string>;

    /**
    * Ein Observable zur Benachrichtigung über den Wechsel eines Tabs
    */
    public onTabChange: KnockoutObservable<boolean>;

    /**
    * Der TabStrip zur Auswahl der Gruppe
    */
    public tabStrip: TabStrip2ViewModelType;

    /**
     * Speichert die gesperrten Ids
     */
    public disabledIds: string[];

    /**
     * Speichert die verborgenen Ids
     */
    public hiddenIds: string[];

    /**
     * Das ViewModel für das RoleAssignment
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(params: IRoleAssignmentViewModelParameters) {
        super(params);

        this.context = this.paramOrDefault(params.context, null);
        this.detailViewItemName = this.paramOrDefault(params.detailViewItemName, 'RoleAssignment');
        this.PrincipalsAndUserGroupsDTO = this.observableOrDefault(params.PrincipalsAndUserGroupsDTO, null).extend({ notify: 'always' });
        this.values = this.createValuesObservable();
        this.names = this.paramOrDefault(params.names, []);
        this.onTabChange = this.observableOrDefault<boolean>(false).extend({ notify: 'always' });
        this.tabStrip = this.createTabStrip();
        // Registriert sich für die FactoryRefreshedMessage und liest initial die Mandanten und Benutzergruppen aus
        this.registerForFactoryRefreshMessage();
        this.readPrincipalsAndUserGroups();
    }
    
    private createValuesFromXml(xml: string): CoreFrameInterfaces.RoleAssignmentValue[] {
        const dto = DataTransferObject.parseFromXML(xml) as MemberOfsDTOType;
        
        let result: CoreFrameInterfaces.RoleAssignmentValue[] = [];
        if (dto.MemberOfs != null) {
            for (let i = 0; i < dto.MemberOfs.length; i++) {
                let MemberOf = dto.MemberOfs[i];

                if (MemberOf.Principals.length > 0) {
                    for (let j = 0; j < MemberOf.Principals.length; j++) {
                        let principalId = MemberOf.Principals[j];

                        result.push({
                            checked: true,
                            member: MemberOf.PDO_ID,
                            principal: principalId.toString(),
                            group: MemberOf.UserGroupPDO_ID.toString()
                        });
                    }
                } else {
                    result.push({
                        checked: true,
                        member: MemberOf.PDO_ID,
                        principal: (-1).toString(),
                        group: MemberOf.UserGroupPDO_ID.toString()
                    });
                }
            }
        }
        
        return result;
    }

    private createValuesObservable(): KnockoutObservable<CoreFrameInterfaces.RoleAssignmentValue[]> {
        const dtoXML = this.context.genericObject.getValue(this.detailViewItemName);
        const values = this.createValuesFromXml(dtoXML);
        const observable = this.observableOrDefault(values).extend({ notify: 'always' });
        
        // Debouncing, damit bei "Alle Mandanten selektieren" nur 1x zum Server gesendet wird
        const debouncedSendToServer = _.debounce((dto: CheckedItemsDTOType) => { 
            return this.sendToServer(dto);
        }, 100);
        
        this.defineSubscription(observable, values => {
            // Daten kommen vom Server, also muss nichts gesendet werden
            if (this.isServerUpdate)
                return;
            
            const dto = this.createDtoFromValues(values);
            debouncedSendToServer(dto);
        });
        return observable;
    }
    
    private createDtoFromValues(values: CoreFrameInterfaces.RoleAssignmentValue[]): CheckedItemsDTOType {
        const dto = new this.CheckedItemsDTOModel();

        if (!values[0]) // Es kann passieren, das hier ein Array mit null vorliegt
            return undefined;

        for (let i = 0; i < values.length; i++) {
            const value = values[i];

            let item = new this.CheckedItemDTOModel();
            item.PDO_ID = value.member;
            item.IsChecked = value.checked;
            item.UserGroupPDO_ID = +value.group;
            item.PrincipalPDO_ID = +value.principal;

            dto.Items.push(item);
        }
        
        return dto;
    }
    
    private isServerUpdate = false;

    /**
     * Sendet die ausgewählten Items zum Server. Dies wird bewusst manuell durchgeführt, anstatt das 
     * Observable des GenericObjects zu beschreiben, da es hier zu Timing-Problemen kommen kann, die dazu führen, 
     * dass Werte nach dem erneuten Öffnen der DetailView nicht so vorzufinden sind, wie sie ausgewählt wurden.
     */
    private sendToServer(dto: CheckedItemsDTOType) {
        const xml = DataTransferObject.toXMLString(dto);
        this.increaseBusyCounter();
        RADEnvironmentProxy.getInstance().updateProperties({
            pdoID: this.context.genericObject.pdoId,
            values: JSON.stringify({ [this.detailViewItemName]: xml }),
            nestedContextName: this.context.getNestedContextPath(),
            scopedProperties: [],
        }).then(result => {
            if (!this.validateStatus(result))
                return;
            
            const xml = result.refreshProperties[this.detailViewItemName];
            const values = this.createValuesFromXml(xml);
            
            try {
                this.isServerUpdate = true;
                this.values(values);
            } finally {
                this.isServerUpdate = false;
            }
            
            this.context.genericObject.hasPendingModifications(true);
        }).always(() => {
            this.decreaseBusyCounter();
        })
    }

    private createTabStrip(): TabStrip2ViewModelType {
        let toDispose = [];
        let tabs = this.defineComputed(() => {
            toDispose.forEach(tab => {
                tab.__createdFactories.forEach(x => x.dispose());
                tab.dispose();
            });
            toDispose = [];

            let result = [];
            const PrincipalsAndUserGroupsDTO = this.PrincipalsAndUserGroupsDTO();

            if (PrincipalsAndUserGroupsDTO != null) {
                this.names = ['System'];

                for (let i = 0; i < this.names.length; i++) {
                    let tabStripTab = new this.TabStripTab2ViewModel({
                        context: this.context,
                        detailViewItems: [
                            {
                                name: this.names[i],
                                uiGenerator: 'RoleAssignmentList'
                            } as IDVGItemDTO
                        ],
                        link: new this.LinkViewModel({
                            onClick: () => {
                                this.tabStrip.selected(tabStripTab);
                                this.onTabChange(true);
                            },
                            label: new this.LabelViewModel({
                                ergName: this.getErgName(i),
                                tryToLoad: false
                            })
                        }),
                        tabStrip: this.tabStrip,
                        isDisplayed: this.isDisplayed
                    });

                    result.push(tabStripTab);
                    toDispose.push(tabStripTab);
                }
            }

            return result;
        });

        const tabstrip = new this.TabStrip2ViewModel({
            tabs: tabs,
            showTabs: this.definePureComputed(() => { return tabs().length > 1; })
        });

        tabs().forEach(t => t.tabStrip = tabstrip);

        return tabstrip;
    }


    private getErgName(index: number): string {
        let ergName = this.names[index];

        if (ergName !== 'System') {
            const key = 'MODULE_NAME_' + ergName.toUpperCase();
            if (ResourceManager.getInstance().get(key) != key) {
                ergName = ResourceManager.getInstance().get(key);
            }
        }

        return ergName;
    }

    /**
    * Registriert sich für die FactoryRefreshMessage
    */
    private registerForFactoryRefreshMessage(): void {
        this.registerForMessageId('FactoryRefreshedMessage', (message: FactoryRefreshedMessage) => {
            if (this.isDisposed === false && message.item.name === 'AccessOnPrincipals') {
                this.readPrincipalsAndUserGroups();
            }
        });
    }

    /**
    * Liest die Mandanten und Gruppen aus
    */
    private readPrincipalsAndUserGroups(): JQueryPromise<void> {
        const pdoId = this.context.genericObject.pdoId;
        const operationName = 'GetPrincipalsAndUserGroups';
        const operationParameters = [];
        const contextParameters = {};

        return RADEnvironmentProxy.getInstance().callOperation({
            pdoID: pdoId,
            operationName: operationName,
            operationParameters: operationParameters,
            contextParameters: contextParameters
        }).then((operationResult) => {
            if (this.isDisposed === false) {
                const dto = DataTransferObject.parseFromXML(operationResult.returnValue) as PrincipalsAndUserGroupsDTO;
                this.PrincipalsAndUserGroupsDTO(dto);
            }
        }).promise();
    }

    public reload(): JQueryPromise<void> {
        return this.readPrincipalsAndUserGroups();
    }
}

ComponentManager.setViewModel('RoleAssignmentViewModel', RoleAssignmentViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');